import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { getAllMarkets } from './hooks/useMarkets';
import { useMarketRegulatoryCardStatuses } from './hooks/useMarketRegulatoryCardStatuses';
import type { MarketRecord } from './types';
import MarketLandingPageEditor from './MarketLandingPageEditor';

function useMarketCatalog() {
    const [markets, setMarkets] = useState<MarketRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [request, setRequest] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);
        getAllMarkets().then((data) => {
            if (!cancelled) setMarkets([...data].sort((first, second) => first.name.localeCompare(second.name)));
        }).catch(() => {
            if (!cancelled) setError(true);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [request]);

    return { markets, loading, error, retry: () => setRequest((value) => value + 1) };
}

function RegulatoryCardStatus({ status }: { status: boolean | null | undefined }) {

    const label = status === undefined ? 'Checking...' : status === null ? 'Unknown' : status ? 'Enabled' : 'Disabled';
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: status === true ? 'var(--success, #16834a)' : 'var(--muted, #777)' }} />
            MCA regulatory card: {label}
        </span>
    );
}

function CatalogState({ loading, error, retry }: { loading: boolean; error: boolean; retry: () => void }) {
    if (loading) return <p role="status">Loading markets...</p>;
    if (error) return (
        <div className="stack" role="alert">
            <p>Unable to load markets.</p>
            <div><button type="button" className="button" onClick={retry}>Try again</button></div>
        </div>
    );
    return null;
}

export default function LandingPagesAdminPage() {
    const catalog = useMarketCatalog();
    const statuses = useMarketRegulatoryCardStatuses();
    return (
        <div className="stack" data-testid="page-internal-landing-pages">
            <PageHeader title="Landing Pages" actions={<Link to="/internal">Back to Internal</Link>} />
            <CatalogState {...catalog} />
            {!catalog.loading && !catalog.error && (
                catalog.markets.length ? (
                    <ul className="stack" aria-label="Markets" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {catalog.markets.map((market) => (
                            <li key={market.id}>
                                <Link
                                    className="card"
                                    to={`/internal/landing-pages/${market.id}/edit`}
                                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', color: 'inherit', textDecoration: 'none' }}
                                >
                                    <span style={{ overflowWrap: 'anywhere' }}><strong>{market.name}</strong>{market.code && <span className="muted"> ({market.code})</span>}</span>
                                    <RegulatoryCardStatus status={statuses === null ? undefined : statuses[market.id] ?? null} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                ) : <p>No markets available.</p>
            )}
        </div>
    );
}

export function LandingPageEditorPage() {
    const { marketId } = useParams();
    const catalog = useMarketCatalog();
    const market = catalog.markets.find((item) => String(item.id) === marketId);
    return (
        <div className="stack" data-testid="page-internal-landing-page-editor">
            <PageHeader title="Landing Page Editor" subtitle={market?.name} actions={<Link to="/internal/landing-pages">Back to Landing Pages</Link>} />
            <CatalogState {...catalog} />
            {!catalog.loading && !catalog.error && (
                market ? <MarketLandingPageEditor key={market.id} marketId={market.id} marketName={market.name} marketCode={market.code} /> : <p role="alert">Market not found.</p>
            )}
        </div>
    );
}
